import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { getPublicPairings } from '@/lib/api';
import { TvQrCode } from '@/components/TvQrCode';
import { formatStartedAgo } from '@/lib/relativeTime';
import type { PublicPairingsResponse } from '@swiss-magic/shared-types';
import { getGameConfig } from '@swiss-magic/shared-types';

const CYCLE_MS = 30_000;

function playerName(p: { firstName: string; lastName: string }): string {
  return `${p.firstName} ${p.lastName}`.trim();
}

export function TvPairingsPage() {
  const { t: tt } = useTranslation('tournament');
  const { slug, roundNumber: roundParam } = useParams<{ slug: string; roundNumber: string }>();
  const roundNumber = Number(roundParam);
  // Translate canonical color names from the game config.
  function colorLabel(canonical: string): string {
    const slug = canonical.toLowerCase().replace(/\s+/g, '_');
    const key = `pairings_public.color.${slug}`;
    const translated = tt(key);
    return translated === key ? canonical : translated;
  }
  const [data, setData] = useState<PublicPairingsResponse | null>(null);
  const [offset, setOffset] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableSectionElement>(null);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  // Fetch data with polling
  useEffect(() => {
    if (!slug || !roundNumber) return;
    const load = () => getPublicPairings(slug, roundNumber).then(setData).catch(() => {});
    load();
    const interval = setInterval(load, 5_000);
    return () => clearInterval(interval);
  }, [slug, roundNumber]);

  // Measure actual row height from DOM
  const measureRows = useCallback(() => {
    if (!containerRef.current) return;
    const tableWrapper = containerRef.current.querySelector('.overflow-hidden');
    const firstRow = tableRef.current?.querySelector('tr');
    if (!tableWrapper || !firstRow) return;
    const available = tableWrapper.clientHeight;
    const rowHeight = firstRow.getBoundingClientRect().height || 36;
    const theadHeight = containerRef.current.querySelector('thead')?.getBoundingClientRect().height || 32;
    setRowsPerPage(Math.max(5, Math.floor((available - theadHeight) / rowHeight)));
  }, []);

  useEffect(() => {
    const t1 = setTimeout(measureRows, 100);
    const t2 = setTimeout(measureRows, 500);
    window.addEventListener('resize', measureRows);
    return () => { clearTimeout(t1); clearTimeout(t2); window.removeEventListener('resize', measureRows); };
  }, [measureRows, data]);

  const totalRef = useRef(0);
  useEffect(() => {
    totalRef.current = data?.pairings.length ?? 0;
  }, [data]);

  // Auto-cycle — depends only on rowsPerPage (not data, to avoid resetting timer on poll)
  useEffect(() => {
    if (rowsPerPage >= 100) return;
    const timer = setInterval(() => {
      const total = totalRef.current;
      if (total <= rowsPerPage) return;
      setOffset((prev) => {
        const next = prev + rowsPerPage;
        return next >= total ? 0 : next;
      });
    }, CYCLE_MS);
    return () => clearInterval(timer);
  }, [rowsPerPage]);

  if (!data) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <p className="text-lg text-muted-foreground">{tt('loading')}</p>
      </div>
    );
  }

  const config = getGameConfig(data.game);
  const visibleRows = data.pairings.slice(offset, offset + rowsPerPage);
  const totalPages = Math.ceil(data.pairings.length / rowsPerPage);
  const currentPage = Math.floor(offset / rowsPerPage) + 1;

  return (
    <div ref={containerRef} className="h-screen bg-background p-4 flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-xl font-bold">{data.tournamentName}</h1>
          <p className="text-sm text-muted-foreground">{tt('common.round_n_label', { roundNumber: data.roundNumber })}</p>
        </div>
        {totalPages > 1 && (
          <span className="text-sm text-muted-foreground">
            {currentPage} / {totalPages}
          </span>
        )}
      </div>

      <div className="flex-1 overflow-hidden rounded border">
        <table className="w-full text-left">
          <thead className="border-b bg-muted/50">
            <tr>
              <th className="w-10 px-2 py-1.5 text-xs font-medium text-muted-foreground">{tt('pairings_public.table.table_number')}</th>
              <th className="px-2 py-1.5 text-xs font-medium text-muted-foreground">{colorLabel(config.colorNames.first)}</th>
              <th className="w-16 px-2 py-1.5 text-center text-xs font-medium text-muted-foreground">{tt('pairings_public.table.result')}</th>
              <th className="px-2 py-1.5 text-xs font-medium text-muted-foreground">{colorLabel(config.colorNames.second)}</th>
              <th className="w-24 px-2 py-1.5 text-xs font-medium text-muted-foreground">{tt('pairings_public.table.status')}</th>
            </tr>
          </thead>
          <tbody ref={tableRef}>
            {visibleRows.map((pairing) => {
              const isBye = pairing.blackPlayer === null;
              const isConfirmed = pairing.resultStatus === 'confirmed';
              const whiteWon = isConfirmed && (pairing.result === '1-0' || pairing.result === '+/-');
              const blackWon = isConfirmed && (pairing.result === '0-1' || pairing.result === '-/+');
              return (
                <tr
                  key={pairing.tableNumber}
                  className={`border-b ${!isConfirmed && !isBye ? 'bg-muted/10' : ''}`}
                >
                  <td className="px-2 py-2 text-sm text-muted-foreground">
                    {pairing.tableNumber}
                  </td>
                  <td className={`px-2 py-2 text-sm ${whiteWon ? 'font-semibold' : ''}`}>
                    {playerName(pairing.whitePlayer)}
                  </td>
                  <td className="px-2 py-2 text-center text-sm font-mono font-semibold">
                    {isBye ? (
                      <span className="text-xs text-muted-foreground">{tt('pairings_public.bye_label')}</span>
                    ) : isConfirmed ? (
                      pairing.result
                    ) : (
                      <span className="text-muted-foreground">{tt('pairings_public.round_dash')}</span>
                    )}
                  </td>
                  <td className={`px-2 py-2 text-sm ${blackWon ? 'font-semibold' : ''}`}>
                    {isBye ? (
                      <span className="text-muted-foreground italic text-xs">{tt('pairings_public.bye_lowercase')}</span>
                    ) : (
                      playerName(pairing.blackPlayer!)
                    )}
                  </td>
                  <td className="px-2 py-2">
                    {pairing.resultStatus === 'confirmed' && (
                      <Badge variant="default" className="bg-green-600 text-xs">{tt('pairings_public.result_status.confirmed')}</Badge>
                    )}
                    {pairing.resultStatus === 'pending' && (
                      <Badge variant="secondary" className="text-xs text-amber-600">{tt('pairings_public.result_status.pending')}</Badge>
                    )}
                    {pairing.resultStatus === 'none' && pairing.startedAt && !isBye && (
                      <Badge
                        variant="default"
                        className="bg-blue-600 text-xs"
                        title={formatStartedAgo(pairing.startedAt)}
                      >{tt('pairings_public.result_status.in_progress')}</Badge>
                    )}
                    {pairing.resultStatus === 'none' && !pairing.startedAt && !isBye && (
                      <Badge variant="outline" className="text-xs text-muted-foreground">{tt('pairings_public.result_status.none')}</Badge>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {slug && <TvQrCode slug={slug} reportPath={`pairings/${roundNumber}`} />}
    </div>
  );
}
